import React from 'react';
import { images, calendlyLink } from '../mock';

const AboutPage = () => {
  return (
    <div>
      {/* About Tara Section */}
      <section className="section-padding">
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 0.8fr',
            gap: '60px',
            alignItems: 'start'
          }} className="responsive-grid">
            <div>
              <h1 className="hero-large" style={{ marginBottom: '24px' }}>
                About Tara
              </h1>
              <p className="body-large" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '24px' }}>
                Tara Phillips isn't your typical hypnotherapist — and she's definitely not in the business of quick fixes or motivational soundbites. From her global practice based in Bangalore, she's built Get Lucid into something far more precise: a space where high-performing individuals come to recalibrate the internal patterns shaping their results, relationships, and identity.
              </p>
              <p className="body-large" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '24px' }}>
                Trained through Mindvalley and a practicing student of neuroscience and psychology, Tara works at the subconscious level — the place where fear, self-doubt, habits, and performance ceilings are actually formed. She positions herself as an Elite Subconscious Performance Architect, because instead of tweaking behaviour, she helps restructure the internal architecture driving it.
              </p>
              <p className="body-large" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '24px' }}>
                What makes her work compelling is that it's personal.
              </p>
              <p className="body-large" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '24px' }}>
                Before founding Get Lucid, Tara went through her own profound transformation. What started as experimenting with sleep hypnosis during a period of anxiety, confusion, and searching for purpose became a disciplined deep dive into the subconscious mind. That journey reshaped her life — emotionally, mentally, and professionally — and ultimately became the foundation of her methodology.
              </p>
              <p className="body-large" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '24px' }}>
                In 2025, she published <a href="https://www.amazon.in/God-Subconscious-Tara-Phillips/dp/8199672013" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-primary)', fontWeight: 500, textDecoration: 'underline' }}><em>God in the Subconscious: A Hypnotherapist's Guide to the Divine</em></a>, a memoir-meets-practical-guide that traces her path through awakening, therapy, hypnosis, and radical self-inquiry. It's thoughtful, grounded, and reflective — exploring where neuroscience meets mysticism without losing discernment.
              </p>
              <p className="body-large" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                Today, entrepreneurs, executives, creatives, and quietly ambitious people seek her out not for hype — but for precision. Every engagement is bespoke. No scripts. No generic protocols. Just a structured, evidence-informed framework designed to create sustainable internal change.
              </p>
            </div>
            <div style={{ position: 'sticky', top: '120px' }}>
              <img
                src={images.aboutTara}
                alt="Tara Phillips with her book"
                style={{
                  width: '100%',
                  maxWidth: '380px',
                  height: 'auto',
                  objectFit: 'cover',
                  borderRadius: '0px'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Get Lucid */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <h2 className="heading-1" style={{ marginBottom: '32px' }}>
            About Get Lucid
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '900px', lineHeight: '1.8', marginBottom: '24px' }}>
            Get Lucid was created as a space for clear, structured inner work.
          </p>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '900px', lineHeight: '1.8', marginBottom: '24px' }}>
            The word <em>lucid</em> means clarity — the ability to think clearly, especially after confusion or overwhelm. That definition reflects the core of this practice.
          </p>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '900px', lineHeight: '1.8', marginBottom: '24px' }}>
            Many of the challenges people experience — anxiety, compulsive behaviors, emotional reactivity, self-doubt — are not personality flaws. They are conditioned responses shaped by past experiences, nervous system imprinting, and subconscious belief formation.
          </p>
          <p className="body-large" style={{ color: 'var(--text-primary)', maxWidth: '900px', lineHeight: '1.8', fontWeight: 500 }}>
            At Get Lucid, we work directly with those conditioning patterns.
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding">
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '80px'
          }} className="responsive-grid">
            <div>
              <h2 className="heading-2" style={{ marginBottom: '24px' }}>
                Mission
              </h2>
              <p className="body-large" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '16px' }}>
                At Get Lucid, our mission is to guide individuals through personalized hypnotherapy sessions that help them break free from fear, anxiety, and conditioned negative patterns.
              </p>
              <p className="body-large" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                We integrate subconscious reprocessing techniques with practical psychological tools so clients develop emotional resilience, cognitive flexibility, and self-trust.
              </p>
            </div>

            <div>
              <h2 className="heading-2" style={{ marginBottom: '24px' }}>
                Vision
              </h2>
              <p className="body-large" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '16px' }}>
                Our vision is to empower individuals to gain a deeper understanding of their psychological and emotional conditioning, move beyond purely symptom-based treatment models, and reclaim personal agency.
              </p>
              <p className="body-large" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                Through integrative subconscious work, we aim to facilitate measurable breakthroughs — mentally, professionally, physically, and spiritually.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <h2 className="heading-1" style={{ marginBottom: '24px' }}>
            The Integrative Approach
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '900px', marginBottom: '48px' }}>
            This is not surface-level coaching. It is integrative subconscious work combining:
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px'
          }}>
            <div style={{ padding: '32px', background: 'var(--bg-primary)', borderRadius: '0px' }}>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>Clinical Foundation</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Clinical hypnotherapy, psychotherapy-informed frameworks, and EMDR-based trauma processing
              </p>
            </div>

            <div style={{ padding: '32px', background: 'var(--bg-primary)', borderRadius: '0px' }}>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>Somatic Practices</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Breathwork for nervous system regulation, somatic movement practices, and Qi Gong-inspired energy regulation
              </p>
            </div>

            <div style={{ padding: '32px', background: 'var(--bg-primary)', borderRadius: '0px' }}>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>Expanded Work</h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Inner child therapy, future progression work, womb regression, past life regression (experience-based), and sound healing
              </p>
            </div>
          </div>
          <p className="body-large" style={{ color: 'var(--text-primary)', maxWidth: '900px', marginTop: '48px', fontWeight: 500 }}>
            Clarity is not mystical. It is neurological, emotional, and behavioral alignment.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding">
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="heading-1" style={{ marginBottom: '24px' }}>
            AWAKEN YOUR POTENTIAL
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto 32px' }}>
            Step into a life of limitless possibilities.
          </p>
          <a
            href={calendlyLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: 'transparent',
              color: 'var(--interactive-base)',
              border: '1px solid var(--interactive-base)',
              borderRadius: '0px',
              padding: '19px 23px',
              minWidth: '210px',
              height: '60px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            className="btn-primary"
          >
            Book Your Discovery Session
          </a>
        </div>
      </section>

      <style>{`
        .btn-primary:hover {
          background: var(--interactive-base);
          color: var(--bg-primary);
        }
        @media (max-width: 1024px) {
          .responsive-grid {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AboutPage;
